import type { ICianMFUIElement } from '@cian/mf-registry/browser';
import type * as React from 'react';

export const MICROFRONTEND_API_POLL_INTERVAL = 16;
export const MICROFRONTEND_API_TIMEOUT = 2000;

interface IWaitForMicrofrontendApiOptions {
  interval?: number;
  timeout?: number;
}

/**
 * Дожидается локального API микрофронтенда.
 *
 * Реестр монтирует инстанс цепочкой асинхронных шагов
 * (`connectedCallback` -> ожидание дочерних элементов -> `runtime.ensure()` -> очередь обновлений -> `update()`),
 * поэтому сразу после рендера `<Microfrontend />` инстанса ещё нет. Публичного сигнала готовности
 * у `ICianMFUIElement` нет (`updatePromise` в `CianMFUIElement` приватный), так что опрашиваем
 * публичный геттер `instance` до появления API.
 *
 * @returns API микрофронтенда либо `null`, если инстанс не поднялся за `timeout`.
 */
export async function waitForMicrofrontendApi<TApi>(
  ref: React.RefObject<ICianMFUIElement | null>,
  {
    interval = MICROFRONTEND_API_POLL_INTERVAL,
    timeout = MICROFRONTEND_API_TIMEOUT,
  }: IWaitForMicrofrontendApiOptions = {},
): Promise<TApi | null> {
  const attempts = Math.max(1, Math.ceil(timeout / interval));

  for (let attempt = 0; attempt < attempts; attempt++) {
    const api = (ref.current?.instance?.customElement as unknown as { api?: TApi } | undefined)?.api;

    if (api) {
      return api;
    }

    await new Promise(resolve => setTimeout(resolve, interval));
  }

  return null;
}
